import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Flag, 
  Clock, 
  Save, 
  Eye, 
  EyeOff, 
  Maximize2, 
  Minimize2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ArrowLeft,
  ArrowRight,
  Home,
  Settings,
  RotateCcw,
  User,
  Download,
  ChevronDown,
  Triangle,
  Circle,
  Bookmark,
  Calculator,
  FileText
} from 'lucide-react';
import { QuestionComponent } from './QuestionTypes';
import KatexRenderer from './KatexRenderer';
import MarkingSchemeDisplay from './MarkingSchemeDisplay';
import QuestionBookmarkButton from './QuestionBookmarkButton';
import { useRealtime } from '../contexts/RealtimeContext';
import { 
  bookmarkQuestion, 
  removeQuestionBookmark, 
  checkQuestionBookmarkStatus 
} from '../firebase/collections';
import WatermarkedQuestion from './antiCheating/WatermarkedQuestion';

// Isolated Calculator Modal Component to prevent re-renders
const CalculatorModal = React.memo(({
  display,
  expression,
  onClose,
  onNumber,
  onOperator,
  onEquals,
  onClear,
  onBackspace,
  onChangeSign,
  onSquareRoot,
  onPercentage,
  onReciprocal,
  onMemoryClear,
  onMemoryRecall,
  onMemoryStore,
  onMemoryAdd,
  onMemorySubtract
}) => {
  const CalcButton = React.memo(({ onClick, className, children, ...props }) => {
    const handleClick = (e) => {
      e.stopPropagation();
      if (onClick) {
        onClick();
      }
    };

    return (
      <button
        type="button"
        onClick={handleClick}
        className={`rounded-md sm:rounded-lg shadow-md font-semibold text-base sm:text-lg py-2.5 sm:py-3 min-h-[44px] transition-colors select-none touch-manipulation ${className}`}
        style={{ WebkitTapHighlightColor: 'transparent' }}
        {...props}
      >
        {children}
      </button>
    );
  }, (prev, next) => prev.onClick === next.onClick && prev.className === next.className && prev.children === next.children);

  // Memoized number handlers
  const handle0 = useCallback(() => onNumber('0'), [onNumber]);
  const handle1 = useCallback(() => onNumber('1'), [onNumber]);
  const handle2 = useCallback(() => onNumber('2'), [onNumber]);
  const handle3 = useCallback(() => onNumber('3'), [onNumber]);
  const handle4 = useCallback(() => onNumber('4'), [onNumber]);
  const handle5 = useCallback(() => onNumber('5'), [onNumber]);
  const handle6 = useCallback(() => onNumber('6'), [onNumber]);
  const handle7 = useCallback(() => onNumber('7'), [onNumber]);
  const handle8 = useCallback(() => onNumber('8'), [onNumber]);
  const handle9 = useCallback(() => onNumber('9'), [onNumber]);
  const handleDecimal = useCallback(() => onNumber('.'), [onNumber]);
  const handlePlus = useCallback(() => onOperator('+'), [onOperator]);
  const handleMinus = useCallback(() => onOperator('-'), [onOperator]);
  const handleMultiply = useCallback(() => onOperator('*'), [onOperator]);
  const handleDivide = useCallback(() => onOperator('/'), [onOperator]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4" onClick={onClose}>
      <div className="bg-gray-100 rounded-lg sm:rounded-xl shadow-2xl w-full max-w-sm mx-auto max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Calculator Header */}
        <div className="bg-blue-600 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-t-lg sm:rounded-t-xl sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <span className="font-medium text-sm sm:text-base">Normal Calculator</span>
            <button
              type="button"
              onClick={onClose}
              className="text-white hover:text-gray-200 p-1 -mr-1 touch-manipulation"
              aria-label="Close calculator"
            >
              <XCircle className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        {/* Calculator Display */}
        <div className="bg-white p-3 sm:p-4">
          <div className="bg-white border border-gray-300 rounded px-2 sm:px-3 py-1.5 sm:py-2 mb-2 min-h-[28px] sm:min-h-[30px] text-right text-xs sm:text-sm text-gray-600 overflow-x-auto">
            {expression || '\u00A0'}
          </div>
          <div className="bg-white border border-gray-300 rounded px-2 sm:px-3 py-2 sm:py-3 min-h-[48px] sm:min-h-[50px] text-right text-xl sm:text-2xl font-semibold text-black overflow-x-auto">
            {display}
          </div>
        </div>

        {/* Calculator Buttons */}
        <div className="p-3 sm:p-4 bg-gray-100 rounded-b-lg sm:rounded-b-xl">
          <div className="grid grid-cols-5 gap-1.5 sm:gap-2 auto-rows-fr">
            {/* Memory Buttons Row */}
            <CalcButton onClick={onMemoryClear} className="bg-gray-400 hover:bg-gray-500 text-black">MC</CalcButton>
            <CalcButton onClick={onMemoryRecall} className="bg-gray-400 hover:bg-gray-500 text-black">MR</CalcButton>
            <CalcButton onClick={onMemoryStore} className="bg-gray-400 hover:bg-gray-500 text-black">MS</CalcButton>
            <CalcButton onClick={onMemoryAdd} className="bg-gray-400 hover:bg-gray-500 text-black">M+</CalcButton>
            <CalcButton onClick={onMemorySubtract} className="bg-gray-400 hover:bg-gray-500 text-black">M-</CalcButton>

            {/* Clear/Special Row */}
            <CalcButton onClick={onBackspace} className="bg-red-500 hover:bg-red-600 text-white">←</CalcButton>
            <CalcButton onClick={onClear} className="bg-red-500 hover:bg-red-600 text-white">C</CalcButton>
            <CalcButton onClick={onChangeSign} className="bg-red-500 hover:bg-red-600 text-white">+/-</CalcButton>
            <CalcButton onClick={onSquareRoot} className="bg-gray-400 hover:bg-gray-500 text-black">√</CalcButton>
            <CalcButton onClick={onPercentage} className="bg-gray-400 hover:bg-gray-500 text-black">%</CalcButton>

            {/* Number Pad Row 1 */}
            <CalcButton onClick={handle7} className="bg-gray-400 hover:bg-gray-500 text-black">7</CalcButton>
            <CalcButton onClick={handle8} className="bg-gray-400 hover:bg-gray-500 text-black">8</CalcButton>
            <CalcButton onClick={handle9} className="bg-gray-400 hover:bg-gray-500 text-black">9</CalcButton>
            <CalcButton onClick={handleDivide} className="bg-gray-400 hover:bg-gray-500 text-black">/</CalcButton>
            <CalcButton onClick={onReciprocal} className="bg-green-500 hover:bg-green-600 text-white">1/x</CalcButton>

            {/* Number Pad Row 2 */}
            <CalcButton onClick={handle4} className="bg-gray-400 hover:bg-gray-500 text-black">4</CalcButton>
            <CalcButton onClick={handle5} className="bg-gray-400 hover:bg-gray-500 text-black">5</CalcButton>
            <CalcButton onClick={handle6} className="bg-gray-400 hover:bg-gray-500 text-black">6</CalcButton>
            <CalcButton onClick={handleMultiply} className="bg-gray-400 hover:bg-gray-500 text-black">*</CalcButton>
            <CalcButton onClick={onEquals} className="bg-green-500 hover:bg-green-600 text-white" style={{ gridRow: 'span 2', gridColumn: '5' }}>=</CalcButton>

            {/* Number Pad Row 3 */}
            <CalcButton onClick={handle1} className="bg-gray-400 hover:bg-gray-500 text-black">1</CalcButton>
            <CalcButton onClick={handle2} className="bg-gray-400 hover:bg-gray-500 text-black">2</CalcButton>
            <CalcButton onClick={handle3} className="bg-gray-400 hover:bg-gray-500 text-black">3</CalcButton>
            <CalcButton onClick={handleMinus} className="bg-gray-400 hover:bg-gray-500 text-black">-</CalcButton>

            {/* Number Pad Row 4 */}
            <CalcButton onClick={handle0} className="bg-gray-400 hover:bg-gray-500 text-black col-span-2">0</CalcButton>
            <CalcButton onClick={handleDecimal} className="bg-gray-400 hover:bg-gray-500 text-black">.</CalcButton>
            <CalcButton onClick={handlePlus} className="bg-gray-400 hover:bg-gray-500 text-black">+</CalcButton>
          </div>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if display or expression changes (handlers are stable)
  return prevProps.display === nextProps.display && 
         prevProps.expression === nextProps.expression;
});

CalculatorModal.displayName = 'CalculatorModal';

const EnhancedTestInterface = ({ 
  questions, 
  answers, 
  onAnswerSelect, 
  onTestSubmit,
  testInfo,
  timeLeft,
  isSubmitting = false,
  questionTimes = {},
  autoSaveEnabled = true,
  onAutoSaveToggle,
  sessionId = null,
  testId = null,
  onQuestionChange = null
}) => {
  const navigate = useNavigate();
  const { currentUser, userProfile, userData } = useRealtime();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [markedForReview, setMarkedForReview] = useState(new Set());
  const [visitedQuestions, setVisitedQuestions] = useState(new Set());
  const [showReview, setShowReview] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved');
  const [isCurrentQuestionBookmarked, setIsCurrentQuestionBookmarked] = useState(false);
  const [bookmarkId, setBookmarkId] = useState(null);
  const [isBookmarkLoading, setIsBookmarkLoading] = useState(false);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(null);
  const [filteredQuestions, setFilteredQuestions] = useState(questions);
  const [filteredQuestionIndex, setFilteredQuestionIndex] = useState(0);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showQuestionPaper, setShowQuestionPaper] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [translatedQuestions, setTranslatedQuestions] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);

  // Helper function to extract plain text from HTML while preserving LaTeX
  const extractTextFromHTML = (html) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Helper function to preserve LaTeX expressions during translation
  const preserveLatexInText = (text) => {
    if (!text) return { text: '', latexParts: [] };
    
    const latexParts = [];
    let placeholderIndex = 0;
    
    // Split text into parts: LaTeX blocks and regular text
    // Pattern 1: Display math $$...$$ (must be processed first)
    let processedText = text.replace(/\$\$[\s\S]*?\$\$/g, (match) => {
      const placeholder = `__LATEX_${placeholderIndex++}__`;
      latexParts.push({ placeholder, latex: match });
      return placeholder;
    });
    
    // Pattern 2: Inline math $...$ (handle carefully to avoid breaking nested expressions)
    // Use a stack-based approach to handle nested $ properly
    const parts = [];
    let currentIndex = 0;
    let i = 0;
    
    while (i < processedText.length) {
      // Look for $ that's not escaped
      if (processedText[i] === '$' && (i === 0 || processedText[i-1] !== '\\')) {
        // Check if it's start of $...$ (not $$ which we already handled)
        if (i === 0 || processedText[i-1] !== '$') {
          // Find matching closing $
          let j = i + 1;
          let found = false;
          while (j < processedText.length) {
            if (processedText[j] === '$' && processedText[j-1] !== '\\') {
              // Found matching $
              const latexBlock = processedText.substring(i, j + 1);
              // Add text before LaTeX
              if (i > currentIndex) {
                parts.push({ type: 'text', content: processedText.substring(currentIndex, i) });
              }
              // Add LaTeX placeholder
              const placeholder = `__LATEX_${placeholderIndex++}__`;
              latexParts.push({ placeholder, latex: latexBlock });
              parts.push({ type: 'latex', placeholder });
              currentIndex = j + 1;
              i = j + 1;
              found = true;
              break;
            }
            j++;
          }
          if (!found) {
            i++;
          }
        } else {
          i++;
        }
      } else {
        i++;
      }
    }
    
    // Add remaining text
    if (currentIndex < processedText.length) {
      parts.push({ type: 'text', content: processedText.substring(currentIndex) });
    }
    
    // Reconstruct text with placeholders
    const finalText = parts.map(part => 
      part.type === 'latex' ? part.placeholder : part.content
    ).join('');
    
    return { text: finalText, latexParts };
  };

  // Helper function to restore LaTeX expressions after translation
  const restoreLatexInText = (translatedText, latexParts) => {
    if (!translatedText || !latexParts || latexParts.length === 0) {
      return translatedText;
    }
    
    let result = translatedText;
    latexParts.forEach(({ placeholder, latex }) => {
      result = result.replace(placeholder, latex);
    });
    
    return result;
  };

  // Translation function using multiple fallback methods with LaTeX preservation
  const translateToHindi = async (text) => {
    if (!text || !text.trim()) return '';
    
    // Extract plain text from HTML if needed
    let plainText = extractTextFromHTML(text);
    if (!plainText.trim()) return '';
    
    // Preserve LaTeX expressions
    const { text: textWithoutLatex, latexParts } = preserveLatexInText(plainText);
    
    // If no text to translate (only LaTeX), return original
    if (!textWithoutLatex.trim() || textWithoutLatex.trim().replace(/__LATEX_\d+__/g, '').trim() === '') {
      return plainText;
    }
    
    // Try multiple translation methods in order
    const translationMethods = [
      // Method 1: Direct Google Translate API
      async () => {
        const response = await fetch(
          `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=hi&dt=t&q=${encodeURIComponent(textWithoutLatex)}`,
          {
            method: 'GET',
            mode: 'cors',
            headers: {
              'Accept': 'application/json',
            },
          }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (data && data[0] && data[0][0] && data[0][0][0]) {
          return data[0].map(item => item[0]).join('');
        }
        throw new Error('Invalid response format');
      },
      
      // Method 2: CORS proxy with Google Translate
      async () => {
        const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(`https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=hi&dt=t&q=${encodeURIComponent(textWithoutLatex)}`)}`;
        const response = await fetch(proxyUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const proxyData = await response.json();
        const translatedData = JSON.parse(proxyData.contents);
        if (translatedData && translatedData[0] && translatedData[0][0] && translatedData[0][0][0]) {
          return translatedData[0].map(item => item[0]).join('');
        }
        throw new Error('Invalid response format');
      },
    ];
    
    // Try each method until one succeeds
    let translatedText = '';
    for (let i = 0; i < translationMethods.length; i++) {
      try {
        translatedText = await translationMethods[i]();
        break;
      } catch (error) {
        if (i === translationMethods.length - 1) {
          // All methods failed, return original text with LaTeX preserved
          return plainText;
        }
      }
    }
    
    // Restore LaTeX expressions in translated text
    const finalText = restoreLatexInText(translatedText, latexParts);
    return finalText || plainText;
  };

  // Translate all questions when language switches to Hindi
  const translateQuestions = useCallback(async (questionsToTranslate) => {
    if (!questionsToTranslate || questionsToTranslate.length === 0) return null;
    
    setIsTranslating(true);
    try {
      const translated = await Promise.all(
        questionsToTranslate.map(async (question) => {
          // Translate question text
          const translatedQuestion = await translateToHindi(question.question || '');
          
          // Translate options if they exist
          let translatedOptions = [];
          if (question.options && Array.isArray(question.options)) {
            translatedOptions = await Promise.all(
              question.options.map(async (option) => {
                if (option && option.trim()) {
                  return await translateToHindi(option);
                }
                return option;
              })
            );
          }
          
          // Translate explanation if it exists
          let translatedExplanation = '';
          if (question.explanation && question.explanation.trim()) {
            translatedExplanation = await translateToHindi(question.explanation);
          }
          
          return {
            ...question,
            question: translatedQuestion || question.question,
            options: translatedOptions.length > 0 ? translatedOptions : question.options,
            explanation: translatedExplanation || question.explanation
          };
        })
      );
      
      return translated;
    } catch (error) {
      return null;
    } finally {
      setIsTranslating(false);
    }
  }, []);

  // Handle language change
  const handleLanguageChange = useCallback(async (newLanguage) => {
    setSelectedLanguage(newLanguage);
    
    if (newLanguage === 'Hindi' && testInfo?.enableLanguageSwitch) {
      // Translate all questions (not just current section)
      const translated = await translateQuestions(questions);
      if (translated) {
        setTranslatedQuestions(translated);
      }
    } else {
      // Reset to original questions
      setTranslatedQuestions(null);
    }
  }, [testInfo?.enableLanguageSwitch, questions, translateQuestions]);

  // Update translated questions when section changes (if Hindi is selected)
  useEffect(() => {
    if (selectedLanguage === 'Hindi' && testInfo?.enableLanguageSwitch && translatedQuestions) {
      // Translation already done for all questions, no need to re-translate
      // The questionsToDisplay will automatically use the correct subset
    }
  }, [selectedSectionIndex, selectedLanguage, testInfo?.enableLanguageSwitch, translatedQuestions]);
  const [calcDisplay, setCalcDisplay] = useState('0');
  const [calcExpression, setCalcExpression] = useState('');
  const [calcMemory, setCalcMemory] = useState(0);
  const [calcOperator, setCalcOperator] = useState(null);
  const [calcPreviousValue, setCalcPreviousValue] = useState(null);
  const autoSaveTimeoutRef = useRef(null);
  // Use refs to prevent re-renders on every calculation
  const calcStateRef = useRef({
    display: '0',
    expression: '',
    operator: null,
    previousValue: null,
    memory: 0
  });

  // Initialize calculator state when opened
  useEffect(() => {
    if (showCalculator) {
      calcStateRef.current.display = calcDisplay;
      calcStateRef.current.expression = calcExpression;
      calcStateRef.current.operator = calcOperator;
      calcStateRef.current.previousValue = calcPreviousValue;
      calcStateRef.current.memory = calcMemory;
    }
  }, [showCalculator]);

  // Determine if test has sections - show sections if they exist, regardless of useDynamicSections flag
  const hasSections = testInfo?.sections && Array.isArray(testInfo.sections) && testInfo.sections.length > 0;
  
  // Get questions for current section or all questions
  const getQuestionsForSection = (sectionIndex) => {
    if (!hasSections || sectionIndex === null) {
      return questions;
    }
    
    const section = testInfo.sections[sectionIndex];
    if (!section || !section.startQuestion || !section.endQuestion) {
      return questions;
    }
    
    const start = parseInt(section.startQuestion) - 1; // Convert to 0-based index
    const end = parseInt(section.endQuestion) - 1; // Convert to 0-based index
    
    return questions.slice(start, end + 1);
  };

  // Update filtered questions when section changes
  useEffect(() => {
    // Use translated questions if Hindi is selected, otherwise use original questions
    const sourceQuestions = selectedLanguage === 'Hindi' && translatedQuestions 
      ? translatedQuestions 
      : questions;
    
    if (!hasSections || selectedSectionIndex === null) {
      setFilteredQuestions(sourceQuestions);
      return;
    }
    
    const section = testInfo.sections[selectedSectionIndex];
    if (!section || !section.startQuestion || !section.endQuestion) {
      setFilteredQuestions(sourceQuestions);
      return;
    }
    
    const start = parseInt(section.startQuestion) - 1; // Convert to 0-based index
    const end = parseInt(section.endQuestion) - 1; // Convert to 0-based index
    const sectionQuestions = sourceQuestions.slice(start, end + 1);
    
    setFilteredQuestions(sectionQuestions);
    
    // Reset to first question of section when section changes
    if (sectionQuestions.length > 0) {
      setFilteredQuestionIndex(0);
    }
  }, [selectedSectionIndex, questions, hasSections, testInfo?.sections, selectedLanguage, translatedQuestions]);

  // Initialize: if sections exist, select first section by default
  useEffect(() => {
    // Use translated questions if Hindi is selected, otherwise use original questions
    const sourceQuestions = selectedLanguage === 'Hindi' && translatedQuestions 
      ? translatedQuestions 
      : questions;
    
    if (hasSections && selectedSectionIndex === null) {
      setSelectedSectionIndex(0);
    } else if (!hasSections) {
      // If no sections, show all questions
      setFilteredQuestions(sourceQuestions);
    }
  }, [hasSections, questions, selectedLanguage, translatedQuestions]);

  // Get current question from filtered questions (which will be translated if Hindi is selected)
  const currentQuestion = filteredQuestions[filteredQuestionIndex];
  
  // Get the actual index in the original questions array
  const getActualQuestionIndex = () => {
    if (!hasSections || selectedSectionIndex === null) {
      return filteredQuestionIndex;
    }
    
    const section = testInfo.sections[selectedSectionIndex];
    if (!section || !section.startQuestion) {
      return filteredQuestionIndex;
    }
    
    const start = parseInt(section.startQuestion) - 1;
    return start + filteredQuestionIndex;
  };

  const actualQuestionIndex = getActualQuestionIndex();

  // Notify parent when question changes
  useEffect(() => {
    if (onQuestionChange && currentQuestion) {
      onQuestionChange(actualQuestionIndex, currentQuestion.id);
    }
  }, [actualQuestionIndex, currentQuestion?.id, onQuestionChange]);

  // Check bookmark status when question changes
  useEffect(() => {
    if (currentUser?.uid && currentQuestion?.id) {
      checkBookmarkStatus();
    }
  }, [currentUser?.uid, currentQuestion?.id]);

  const checkBookmarkStatus = async () => {
    if (!currentUser?.uid || !currentQuestion?.id) return;
    try {
      const result = await checkQuestionBookmarkStatus(currentUser.uid, currentQuestion.id);
      if (result.success) {
        setIsCurrentQuestionBookmarked(result.isBookmarked);
        setBookmarkId(result.bookmarkId);
      }
    } catch (error) {
      // Error checking bookmark status
    }
  };

  const handleToggleBookmark = async () => {
    if (!currentUser?.uid || !currentQuestion) {
      return;
    }

    setIsBookmarkLoading(true);
    try {
      if (isCurrentQuestionBookmarked) {
        // Remove bookmark
        const result = await removeQuestionBookmark(bookmarkId);
        if (result.success) {
          setIsCurrentQuestionBookmarked(false);
          setBookmarkId(null);
        }
      } else {
        // Add bookmark
        const questionData = {
          questionId: currentQuestion.id,
          question: currentQuestion.question,
          type: currentQuestion.type || 'multiple-choice',
          options: currentQuestion.options || [],
          correctAnswer: currentQuestion.correctAnswer,
          explanation: currentQuestion.explanation || '',
          topic: currentQuestion.topic || currentQuestion.subject || 'General',
          subject: currentQuestion.subject || currentQuestion.topic || 'General',
          difficulty: currentQuestion.difficulty || 'medium',
          testId: testInfo?.id || 'unknown',
          testName: testInfo?.name || 'Unknown Test',
          markingScheme: currentQuestion.markingScheme || { correct: 4, incorrect: -1, unattempted: 0 }
        };

        const result = await bookmarkQuestion(currentUser.uid, questionData);
        if (result.success) {
          setIsCurrentQuestionBookmarked(true);
          setBookmarkId(result.data.id);
        }
      }
    } catch (error) {
      // Error toggling bookmark
    } finally {
      setIsBookmarkLoading(false);
    }
  };

  // Mark question as visited when navigating
  useEffect(() => {
    if (currentQuestion) {
      setVisitedQuestions(prev => new Set([...prev, currentQuestion.id]));
    }
  }, [filteredQuestionIndex, currentQuestion]);

  // Auto-save functionality
  useEffect(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    if (autoSaveEnabled && Object.keys(answers).length > 0) {
      setAutoSaveStatus('saving');
      
      autoSaveTimeoutRef.current = setTimeout(() => {
        // Simulate auto-save
        setAutoSaveStatus('saved');
        setLastSaved(new Date());
      }, 2000);
    }

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [answers, autoSaveEnabled]);

  // Full screen prevention
  useEffect(() => {
    if (isFullScreen) {
      const preventExit = (e) => {
        e.preventDefault();
        e.returnValue = '';
      };

      window.addEventListener('beforeunload', preventExit);
      document.addEventListener('contextmenu', (e) => e.preventDefault());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'F11' || (e.ctrlKey && e.key === 'f')) {
          e.preventDefault();
        }
      });

      return () => {
        window.removeEventListener('beforeunload', preventExit);
        document.removeEventListener('contextmenu', (e) => e.preventDefault());
      };
    }
  }, [isFullScreen]);

  const handleQuestionJump = (index) => {
    // If sections are active, jump within filtered questions
    if (hasSections && selectedSectionIndex !== null) {
      setFilteredQuestionIndex(index);
    } else {
      // No sections, use direct index
      setFilteredQuestionIndex(index);
    }
  };

  const handlePreviousQuestion = () => {
    if (filteredQuestionIndex > 0) {
      setFilteredQuestionIndex(filteredQuestionIndex - 1);
    }
  };

  const handleNextQuestion = () => {
    if (filteredQuestionIndex < filteredQuestions.length - 1) {
      setFilteredQuestionIndex(filteredQuestionIndex + 1);
    }
  };

  const handleSectionClick = (sectionIndex) => {
    setSelectedSectionIndex(sectionIndex);
  };

  const toggleFlagQuestion = () => {
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(currentQuestion.id)) {
        newSet.delete(currentQuestion.id);
      } else {
        newSet.add(currentQuestion.id);
      }
      return newSet;
    });
  };

  const toggleMarkForReview = () => {
    setMarkedForReview(prev => {
      const newSet = new Set(prev);
      if (newSet.has(currentQuestion.id)) {
        newSet.delete(currentQuestion.id);
      } else {
        newSet.add(currentQuestion.id);
      }
      return newSet;
    });
  };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  const getQuestionStatus = (question) => {
    if (answers[question.id] !== undefined && answers[question.id] !== null && answers[question.id] !== '') {
      return 'answered';
    }
    if (visitedQuestions.has(question.id)) {
      return 'visited'; // Visited but not answered = "Not Answered"
    }
    return 'unvisited'; // Not visited = "Not Visited"
  };

  const getQuestionReviewStatus = (question) => {
    if (markedForReview.has(question.id)) {
      return 'marked-for-review';
    }
    if (flaggedQuestions.has(question.id)) {
      return 'flagged';
    }
    return 'normal';
  };

  // Get the exact category for a question (mutually exclusive)
  const getQuestionCategory = (question) => {
    const isAnswered = answers[question.id] !== undefined && answers[question.id] !== null && answers[question.id] !== '';
    const isMarkedForReview = markedForReview.has(question.id);
    const isVisited = visitedQuestions.has(question.id);

    // Priority order (highest to lowest):
    if (isAnswered && isMarkedForReview) {
      return 'answered-and-marked';
    }
    if (isMarkedForReview) {
      return 'marked-for-review';
    }
    if (isAnswered) {
      return 'answered';
    }
    if (isVisited) {
      return 'not-answered';
    }
    return 'not-visited';
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatQuestionTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmitTest = () => {
    setShowConfirmSubmit(true);
  };

  const confirmSubmit = () => {
    setShowConfirmSubmit(false);
    onTestSubmit();
  };

  const getProgressPercentage = () => {
    const answeredCount = Object.keys(answers).filter(key => answers[key] !== undefined && answers[key] !== null).length;
    const totalQuestions = hasSections ? filteredQuestions.length : questions.length;
    return totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;
  };

  const getAverageTimePerQuestion = () => {
    const totalTime = Object.values(questionTimes).reduce((sum, time) => sum + time, 0);
    const answeredCount = Object.keys(questionTimes).length;
    return answeredCount > 0 ? Math.round(totalTime / answeredCount) : 0;
  };


  const handleNumber = useCallback((num) => {
    const current = calcStateRef.current.display;
    let newValue;
    if (current === '0' || current === 'Error') {
      newValue = num === '.' ? '0.' : num;
    } else {
      if (num === '.' && current.includes('.')) {
        return; // Prevent multiple decimal points
      }
      newValue = current + num;
    }
    calcStateRef.current.display = newValue;
    setCalcDisplay(newValue);
  }, []);

  const handleOperator = useCallback((op) => {
    const state = calcStateRef.current;
    if (state.operator && state.previousValue !== null) {
      // Calculate previous operation first
      const current = parseFloat(state.display);
      let result;
      
      try {
        switch (state.operator) {
          case '+':
            result = state.previousValue + current;
            break;
          case '-':
            result = state.previousValue - current;
            break;
          case '*':
            result = state.previousValue * current;
            break;
          case '/':
            if (current === 0) {
              throw new Error('Division by zero');
            }
            result = state.previousValue / current;
            break;
          default:
            result = current;
        }
        
        result = Math.round(result * 100000000) / 100000000;
        state.display = String(result);
        state.previousValue = result;
        state.operator = op;
        state.expression = String(result) + ' ' + op;
      } catch (error) {
        state.display = 'Error';
        state.previousValue = null;
        state.operator = null;
        state.expression = '';
      }
    } else {
      state.previousValue = parseFloat(state.display);
      state.operator = op;
      state.expression = state.display + ' ' + op;
      state.display = '0';
    }
    
    // Update state once
    setCalcDisplay(state.display);
    setCalcPreviousValue(state.previousValue);
    setCalcOperator(state.operator);
    setCalcExpression(state.expression);
  }, []);

  const handleEquals = useCallback(() => {
    const state = calcStateRef.current;
    if (state.operator && state.previousValue !== null) {
      const current = parseFloat(state.display);
      let result;
      
      try {
        switch (state.operator) {
          case '+':
            result = state.previousValue + current;
            break;
          case '-':
            result = state.previousValue - current;
            break;
          case '*':
            result = state.previousValue * current;
            break;
          case '/':
            if (current === 0) {
              throw new Error('Division by zero');
            }
            result = state.previousValue / current;
            break;
          default:
            result = current;
        }
        
        // Format result to avoid floating point errors
        result = Math.round(result * 100000000) / 100000000;
        state.display = String(result);
        state.expression = state.previousValue + ' ' + state.operator + ' ' + current + ' =';
        state.operator = null;
        state.previousValue = null;
      } catch (error) {
        state.display = 'Error';
        state.expression = '';
        state.operator = null;
        state.previousValue = null;
      }
      
      // Update state once
      setCalcDisplay(state.display);
      setCalcExpression(state.expression);
      setCalcOperator(state.operator);
      setCalcPreviousValue(state.previousValue);
    }
  }, []);

  const handleClear = useCallback(() => {
    calcStateRef.current.display = '0';
    calcStateRef.current.expression = '';
    calcStateRef.current.operator = null;
    calcStateRef.current.previousValue = null;
    setCalcDisplay('0');
    setCalcExpression('');
    setCalcOperator(null);
    setCalcPreviousValue(null);
  }, []);

  const handleBackspace = useCallback(() => {
    const state = calcStateRef.current;
    if (state.display.length > 1) {
      state.display = state.display.slice(0, -1);
    } else {
      state.display = '0';
    }
    setCalcDisplay(state.display);
  }, []);

  const handleChangeSign = useCallback(() => {
    const state = calcStateRef.current;
    if (state.display !== '0' && state.display !== 'Error') {
      state.display = String(-parseFloat(state.display));
      setCalcDisplay(state.display);
    }
  }, []);

  const handleSquareRoot = useCallback(() => {
    const state = calcStateRef.current;
    const value = parseFloat(state.display);
    if (value >= 0) {
      const result = Math.sqrt(value);
      state.display = String(result);
      state.expression = '√(' + state.display + ')';
    } else {
      state.display = 'Error';
      state.expression = '';
    }
    setCalcDisplay(state.display);
    setCalcExpression(state.expression);
  }, []);

  const handlePercentage = useCallback(() => {
    const state = calcStateRef.current;
    const value = parseFloat(state.display);
    const result = value / 100;
    state.display = String(result);
    state.expression = state.display + '%';
    setCalcDisplay(state.display);
    setCalcExpression(state.expression);
  }, []);

  const handleReciprocal = useCallback(() => {
    const state = calcStateRef.current;
    const value = parseFloat(state.display);
    if (value !== 0) {
      const result = 1 / value;
      state.display = String(result);
      state.expression = '1/(' + state.display + ')';
    } else {
      state.display = 'Error';
      state.expression = '';
    }
    setCalcDisplay(state.display);
    setCalcExpression(state.expression);
  }, []);

  // Memory functions
  const handleMemoryClear = useCallback(() => {
    calcStateRef.current.memory = 0;
    setCalcMemory(0);
  }, []);

  const handleMemoryRecall = useCallback(() => {
    const mem = calcStateRef.current.memory;
    calcStateRef.current.display = String(mem);
    setCalcDisplay(String(mem));
  }, []);

  const handleMemoryStore = useCallback(() => {
    const val = parseFloat(calcStateRef.current.display) || 0;
    calcStateRef.current.memory = val;
    setCalcMemory(val);
  }, []);

  const handleMemoryAdd = useCallback(() => {
    const val = calcStateRef.current.memory + (parseFloat(calcStateRef.current.display) || 0);
    calcStateRef.current.memory = val;
    setCalcMemory(val);
  }, []);

  const handleMemorySubtract = useCallback(() => {
    const val = calcStateRef.current.memory - (parseFloat(calcStateRef.current.display) || 0);
    calcStateRef.current.memory = val;
    setCalcMemory(val);
  }, []);

  // Memoized close handler
  const handleCalculatorClose = useCallback(() => {
    handleClear();
    setShowCalculator(false);
  }, [handleClear]);

  // Memoized number handlers to prevent re-renders - defined after all base handlers
  const handleNumber0 = useCallback(() => handleNumber('0'), [handleNumber]);
  const handleNumber1 = useCallback(() => handleNumber('1'), [handleNumber]);
  const handleNumber2 = useCallback(() => handleNumber('2'), [handleNumber]);
  const handleNumber3 = useCallback(() => handleNumber('3'), [handleNumber]);
  const handleNumber4 = useCallback(() => handleNumber('4'), [handleNumber]);
  const handleNumber5 = useCallback(() => handleNumber('5'), [handleNumber]);
  const handleNumber6 = useCallback(() => handleNumber('6'), [handleNumber]);
  const handleNumber7 = useCallback(() => handleNumber('7'), [handleNumber]);
  const handleNumber8 = useCallback(() => handleNumber('8'), [handleNumber]);
  const handleNumber9 = useCallback(() => handleNumber('9'), [handleNumber]);
  const handleNumberDecimal = useCallback(() => handleNumber('.'), [handleNumber]);

  // Memoized operator handlers - defined after handleOperator
  const handleOperatorPlus = useCallback(() => handleOperator('+'), [handleOperator]);
  const handleOperatorMinus = useCallback(() => handleOperator('-'), [handleOperator]);
  const handleOperatorMultiply = useCallback(() => handleOperator('*'), [handleOperator]);
  const handleOperatorDivide = useCallback(() => handleOperator('/'), [handleOperator]);

  return (
    <div className={`min-h-screen bg-white transition-all duration-300 ${isFullScreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Header - JEE Main Style */}
      <div className="bg-white border-b sticky top-0 z-40">
        {/* Top Section: Branding and Candidate Info */}
        <div className="border-b">
          <div className="max-w-full mx-auto px-3 sm:px-4 py-2">
            {/* First Row: Branding and Candidate Info */}
            <div className="flex items-center justify-between mb-2 md:mb-0">
              {/* Left: Mahstry Branding */}
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-xs sm:text-sm">M</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white truncate">MahStry</div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 truncate hidden sm:block">Empowering an Educated World</div>
                </div>
              </div>

              {/* Right: Candidate Info Box and Timer */}
              <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
                {/* Desktop: Calculator and Question Paper buttons */}
                <div className="hidden lg:flex items-center space-x-2">
                  {/* Calculator Button */}
                  {testInfo?.enableCalculator && (
                    <button
                      onClick={() => setShowCalculator(!showCalculator)}
                      className="flex items-center justify-center space-x-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors min-h-[44px]"
                      title="Calculator"
                    >
                      <Calculator className="w-4 h-4 text-orange-400" />
                      <span className="text-sm font-medium">Calculator</span>
                    </button>
                  )}

                  {/* Question Paper Button */}
                  {testInfo?.enableQuestionPaper && (
                    <button
                      onClick={() => setShowQuestionPaper(!showQuestionPaper)}
                      className="flex items-center justify-center space-x-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors min-h-[44px]"
                      title="Question Paper"
                    >
                      <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                        <FileText className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-sm font-medium">Question Paper</span>
                    </button>
                  )}
                </div>

                {/* Candidate Info Box */}
                <div className="bg-white border border-gray-300 rounded px-2 sm:px-3 lg:px-4 py-2 flex-shrink-0">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    {/* User icon and name - hidden on mobile */}
                    <div className="hidden md:flex items-center space-x-2 lg:space-x-3">
                      <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 lg:w-6 lg:h-6 text-gray-600" />
                      </div>
                      <div className="text-xs min-w-0">
                        <div className="text-black font-medium truncate">
                          {userProfile?.displayName || 
                            userProfile?.name || 
                            userData?.dashboard?.displayName || 
                            userData?.dashboard?.name || 
                            currentUser?.displayName || 
                            currentUser?.email?.split('@')[0] || 
                            '[Your Name]'}
                        </div>
                        <div className="text-black font-medium truncate">{testInfo?.name || testInfo?.title || '[Test Practice]'}</div>
                      </div>
                    </div>
                    {/* Timer - visible on all screens, more prominent on mobile */}
                    <div className="bg-blue-600 text-white px-2 sm:px-3 py-2 sm:py-1 rounded flex-shrink-0">
                      <div className="text-xs">Time</div>
                      <div className={`text-sm sm:text-base lg:text-sm font-mono font-bold ${
                        timeLeft < 300 ? 'text-red-300' : 'text-white'
                      }`}>
                        {formatTime(timeLeft)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Second Row: Calculator and Question Paper buttons (Mobile/Tablet only) */}
            {(testInfo?.enableCalculator || testInfo?.enableQuestionPaper) && (
              <div className="flex items-center justify-center space-x-3 lg:hidden">
                {/* Calculator Button */}
                {testInfo?.enableCalculator && (
                  <button
                    onClick={() => setShowCalculator(!showCalculator)}
                    className="flex items-center justify-center px-3 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors min-h-[44px]"
                    title="Calculator"
                  >
                    <Calculator className="w-5 h-5 text-orange-400" />
                  </button>
                )}

                {/* Question Paper Button */}
                {testInfo?.enableQuestionPaper && (
                  <button
                    onClick={() => setShowQuestionPaper(!showQuestionPaper)}
                    className="flex items-center justify-center px-2 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                    title="Question Paper"
                  >
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <FileText className="w-4 h-4 text-white" />
                    </div>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Subject Navigation Tabs - Only show if sections exist */}
        {hasSections ? (
          <div className="bg-orange-500">
            <div className="max-w-full mx-auto px-4 py-1 flex items-center space-x-1">
              <button className="bg-orange-500 text-white px-4 py-2 font-semibold text-sm">
                {(testInfo?.name || testInfo?.title || 'EXAM').toUpperCase()}
              </button>
              {testInfo.sections.map((section, index) => (
                <button 
                  key={index}
                  onClick={() => handleSectionClick(index)}
                  className={`px-4 py-2 font-semibold text-sm transition-colors ${
                    selectedSectionIndex === index
                      ? 'bg-blue-800 text-white'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {section.name?.toUpperCase() || `SECTION ${index + 1}`}
                </button>
              ))}
              <div className="ml-auto flex items-center space-x-2">
                <button 
                  onClick={handleToggleBookmark}
                  disabled={isBookmarkLoading || !currentQuestion}
                  className={`px-3 py-1 rounded flex items-center space-x-1 text-sm transition-colors ${
                    isCurrentQuestionBookmarked
                      ? 'bg-yellow-500 text-black hover:bg-yellow-600'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  } ${isBookmarkLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={isCurrentQuestionBookmarked ? "Remove Bookmark" : "Bookmark Question"}
                >
                  <Bookmark className={`w-4 h-4 ${isCurrentQuestionBookmarked ? 'fill-current' : ''}`} />
                  <span>{isCurrentQuestionBookmarked ? 'BOOKMARKED' : 'BOOKMARK'}</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Bookmark button when no sections */
          <div className="bg-orange-500">
            <div className="max-w-full mx-auto px-4 py-1 flex items-center justify-end">
              <button 
                onClick={handleToggleBookmark}
                disabled={isBookmarkLoading || !currentQuestion}
                className={`px-3 py-1 rounded flex items-center space-x-1 text-sm transition-colors ${
                  isCurrentQuestionBookmarked
                    ? 'bg-yellow-500 text-black hover:bg-yellow-600'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                } ${isBookmarkLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={isCurrentQuestionBookmarked ? "Remove Bookmark" : "Bookmark Question"}
              >
                <Bookmark className={`w-4 h-4 ${isCurrentQuestionBookmarked ? 'fill-current' : ''}`} />
                <span>{isCurrentQuestionBookmarked ? 'BOOKMARKED' : 'BOOKMARK'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Paper Language - Always visible, disabled if not allowed by admin */}
        <div className="bg-white border-b px-4 py-2">
          <div className="flex items-center space-x-2">
            <span className="text-orange-600 text-sm font-medium">Paper Language:</span>
            <div 
              className="relative"
              onClick={() => {
                if (!testInfo?.enableLanguageSwitch) {
                  alert('Language switching is not allowed by admin');
                }
              }}
            >
              <select 
                value={selectedLanguage}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className={`border border-gray-300 rounded px-3 py-1 text-sm ${
                  testInfo?.enableLanguageSwitch 
                    ? 'bg-white cursor-pointer' 
                    : 'bg-gray-100 cursor-not-allowed opacity-60 pointer-events-none'
                }`}
                disabled={!testInfo?.enableLanguageSwitch || isTranslating}
              >
                <option value="English">English</option>
                <option value="Hindi">Hindi</option>
              </select>
              {isTranslating && (
                <span className="text-xs text-blue-600 ml-2">Translating...</span>
              )}
            </div>
            {!testInfo?.enableLanguageSwitch && (
              <span className="text-xs text-gray-500 italic">(Not allowed by admin)</span>
            )}
          </div>
          
          {/* Warning message when Hindi is selected */}
          {selectedLanguage === 'Hindi' && testInfo?.enableLanguageSwitch && (
            <div className="mt-2 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-md">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-yellow-800 font-medium mb-1">
                    Important Notice
                  </p>
                  <p className="text-xs text-yellow-700">
                    The Hindi translation may contain errors in equations, powers, subscripts, and mathematical symbols. 
                    If you have any confusion, please switch back to English to clarify the original question.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3 order-2 lg:order-1">
            {currentQuestion && (
              <div className="bg-white p-3 sm:p-6">
                {/* Question Header - JEE Style */}
                <div className="mb-4 sm:mb-6">
                  <div className="mb-4">
                    <div className="text-lg font-semibold text-black mb-2">
                      Question {hasSections ? (actualQuestionIndex + 1) : (filteredQuestionIndex + 1)}:
                      {hasSections && selectedSectionIndex !== null && (
                        <span className="text-sm font-normal text-gray-600 ml-2">
                          ({testInfo.sections[selectedSectionIndex]?.name || `Section ${selectedSectionIndex + 1}`})
                        </span>
                      )}
                    </div>
                    <div className="h-px bg-gray-300 mb-4"></div>
                  </div>
                  
                  <div className="text-base text-black mb-4 relative">
                    {sessionId && testId ? (
                      <WatermarkedQuestion
                        question={currentQuestion}
                        questionIndex={filteredQuestionIndex}
                        testId={testId}
                        sessionId={sessionId}
                        testInfo={testInfo}
                      />
                    ) : (
                      <KatexRenderer content={currentQuestion.question} />
                    )}
                  </div>
                </div>

                {/* Question Component */}
                <div className="mb-6">
                  <QuestionComponent
                    question={currentQuestion}
                    selectedAnswer={answers[currentQuestion.id]}
                    onAnswerSelect={onAnswerSelect}
                    isReview={false}
                  />
                </div>

                {/* Navigation Buttons - JEE Style */}
                <div className="pt-4 sm:pt-6 border-t space-y-3">
                  {/* Primary Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        onAnswerSelect(currentQuestion?.id, answers[currentQuestion?.id]);
                        handleNextQuestion();
                      }}
                      className="px-4 py-2 bg-green-600 text-white font-semibold text-sm hover:bg-green-700"
                    >
                      SAVE & NEXT
                    </button>
                    <button
                      onClick={() => {
                        onAnswerSelect(currentQuestion?.id, answers[currentQuestion?.id]);
                        toggleMarkForReview();
                        handleNextQuestion();
                      }}
                      className="px-4 py-2 bg-yellow-500 text-black font-semibold text-sm hover:bg-yellow-600"
                    >
                      SAVE & MARK FOR REVIEW
                    </button>
                    <button
                      onClick={() => onAnswerSelect(currentQuestion?.id, null)}
                      className="px-4 py-2 bg-white text-black border border-gray-300 font-semibold text-sm hover:bg-gray-50"
                    >
                      CLEAR RESPONSE
                    </button>
                    <button
                      onClick={() => {
                        toggleMarkForReview();
                        handleNextQuestion();
                      }}
                      className="px-4 py-2 bg-blue-800 text-white font-semibold text-sm hover:bg-blue-900"
                    >
                      MARK FOR REVIEW & NEXT
                    </button>
                  </div>

                  {/* Navigation Buttons */}
                  <div className="flex items-center justify-between">
                    <button
                      onClick={handlePreviousQuestion}
                      disabled={filteredQuestionIndex === 0}
                      className={`px-4 py-2 bg-white text-black border border-gray-300 font-semibold text-sm ${
                        filteredQuestionIndex === 0
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      &lt;&lt; BACK
                    </button>
                    <button
                      onClick={handleNextQuestion}
                      disabled={filteredQuestionIndex === filteredQuestions.length - 1}
                      className={`px-4 py-2 bg-white text-black border border-gray-300 font-semibold text-sm ${
                        filteredQuestionIndex === filteredQuestions.length - 1
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      NEXT &gt;&gt;
                    </button>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end">
                    <button
                      onClick={handleSubmitTest}
                      disabled={isSubmitting}
                      className={`px-6 py-2 bg-green-600 text-white font-semibold text-sm hover:bg-green-700 ${
                        isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Question Navigation */}
          <div className="lg:col-span-1 order-1 lg:order-2">
            <div className="bg-white border border-gray-200 p-3 sm:p-6 sticky top-4 sm:top-8 min-w-[280px]">
                
              {/* Summary Stats - JEE Style with Colored Boxes/Circles - 2 columns */}
              <div className="grid grid-cols-2 gap-2 mb-4 sm:mb-6">
                {/* Not Visited - Gray box with count */}
                <div className="flex items-center text-sm">
                  <div className="w-8 h-8 bg-gray-200 border border-gray-300 rounded flex items-center justify-center mr-2 flex-shrink-0">
                    <span className="text-gray-700 font-semibold text-sm">
                      {filteredQuestions.filter(q => getQuestionCategory(q) === 'not-visited').length}
                    </span>
                  </div>
                  <span className="text-gray-700 text-xs">Not Visited</span>
                </div>
                
                {/* Not Answered - Red box with triangle and count */}
                <div className="flex items-center text-sm">
                  <div className="w-8 h-8 bg-red-500 border border-red-600 rounded flex items-center justify-center mr-2 flex-shrink-0 relative">
                    <span className="text-white font-semibold text-sm">
                      {filteredQuestions.filter(q => getQuestionCategory(q) === 'not-answered').length}
                    </span>
                    <Triangle className="w-2 h-2 text-white fill-white absolute top-0.5 right-0.5" />
                  </div>
                  <span className="text-red-600 text-xs">Not Answered</span>
                </div>
                
                {/* Answered - Green circle with count */}
                <div className="flex items-center text-sm">
                  <div className="w-8 h-8 bg-green-500 border border-green-600 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                    <span className="text-white font-semibold text-sm">
                      {filteredQuestions.filter(q => getQuestionCategory(q) === 'answered').length}
                    </span>
                  </div>
                  <span className="text-green-600 text-xs">Answered</span>
                </div>
                
                {/* Marked for Review - Purple circle with count */}
                <div className="flex items-center text-sm">
                  <div className="w-8 h-8 bg-purple-500 border border-purple-600 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                    <span className="text-white font-semibold text-sm">
                      {filteredQuestions.filter(q => getQuestionCategory(q) === 'marked-for-review').length}
                    </span>
                  </div>
                  <span className="text-purple-600 text-xs">Marked for Review</span>
                </div>
                
                {/* Answered & Marked for Review - Purple circle with green dot and count - Full width */}
                <div className="col-span-2 flex items-center text-sm">
                  <div className="w-8 h-8 bg-purple-700 border border-purple-800 rounded-full flex items-center justify-center mr-2 flex-shrink-0 relative">
                    <span className="text-white font-semibold text-sm">
                      {filteredQuestions.filter(q => getQuestionCategory(q) === 'answered-and-marked').length}
                    </span>
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border border-green-600"></div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-purple-800 text-xs">Answered & Marked for Review</span>
                    <span className="text-purple-800 text-[10px]">(will be considered for evaluation)</span>
                  </div>
                </div>
              </div>

              {/* Section Header if sections exist */}
              {hasSections && selectedSectionIndex !== null && (
                <div className="mb-3 px-2 py-1 bg-blue-50 border border-blue-200 rounded">
                  <div className="text-sm font-semibold text-blue-800">
                    {testInfo.sections[selectedSectionIndex]?.name || `Section ${selectedSectionIndex + 1}`}
                  </div>
                  <div className="text-xs text-blue-600">
                    Questions {testInfo.sections[selectedSectionIndex]?.startQuestion || 1} - {testInfo.sections[selectedSectionIndex]?.endQuestion || filteredQuestions.length}
                  </div>
                </div>
              )}

              {/* Question Grid - 4 columns per row, expanded vertically */}
              <div className="max-h-96 overflow-y-auto mb-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                <div className="grid grid-cols-4 gap-2">
                  {filteredQuestions.map((question, index) => {
                    const status = getQuestionStatus(question);
                    const reviewStatus = getQuestionReviewStatus(question);
                    const isCurrent = index === filteredQuestionIndex;
                    const questionTime = questionTimes[question.id];
                    
                    // Determine the exact status for color coding
                    const isAnswered = status === 'answered';
                    const isMarkedForReview = reviewStatus === 'marked-for-review';
                    const isNotAnswered = status === 'visited' && !isAnswered;
                    const isNotVisited = status === 'unvisited';
                    
                    // Color logic matching JEE Main interface
                    let buttonClass = '';
                    if (isCurrent) {
                      // Current question - Orange background with red border if not answered
                      if (isNotAnswered || (!isAnswered && !isMarkedForReview)) {
                        buttonClass = 'bg-orange-500 text-white border-2 border-red-600';
                      } else if (isAnswered) {
                        buttonClass = 'bg-orange-500 text-white border-2 border-orange-600';
                      } else {
                        buttonClass = 'bg-orange-500 text-white border-2 border-orange-600';
                      }
                    } else if (isAnswered && isMarkedForReview) {
                      // Answered & Marked for Review - Darker purple
                      buttonClass = 'bg-purple-700 text-white border border-purple-800 hover:bg-purple-800';
                    } else if (isMarkedForReview) {
                      // Marked for Review (not answered) - Purple
                      buttonClass = 'bg-purple-500 text-white border border-purple-600 hover:bg-purple-600';
                    } else if (isAnswered) {
                      // Answered - Green
                      buttonClass = 'bg-green-500 text-white border border-green-600 hover:bg-green-600';
                    } else if (isNotAnswered) {
                      // Not Answered (visited but not answered) - Red
                      buttonClass = 'bg-red-500 text-white border border-red-600 hover:bg-red-600';
                    } else {
                      // Not Visited - White background, black text, gray border
                      buttonClass = 'bg-white text-black border border-gray-300 hover:bg-gray-50';
                    }
                    
                    // Get the actual question number (1-based) for display
                    const actualQuestionNumber = hasSections && selectedSectionIndex !== null
                      ? parseInt(testInfo.sections[selectedSectionIndex]?.startQuestion || 1) + index
                      : index + 1;
                    
                    return (
                      <button
                        key={question.id || index}
                        onClick={() => handleQuestionJump(index)}
                        className={`w-12 h-12 rounded text-sm font-bold transition-all duration-200 relative ${buttonClass}`}
                        title={`Question ${actualQuestionNumber} - ${status}${reviewStatus === 'marked-for-review' ? ' (Marked for Review)' : reviewStatus === 'flagged' ? ' (Flagged)' : ''}${questionTime ? ` (${formatQuestionTime(questionTime)})` : ''}`}
                      >
                        {actualQuestionNumber.toString().padStart(2, '0')}
                        {questionTime && (
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 flex items-center justify-center text-xs font-bold text-gray-600 bg-gray-200 rounded-full border border-gray-300">
                            {Math.floor(questionTime / 60)}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Confirm Submit Modal */}
      {showConfirmSubmit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="text-yellow-500" size={24} />
              <h3 className="text-lg font-semibold">Confirm Submission</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to submit your test? You won't be able to make changes after submission.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirmSubmit(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmSubmit}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Submit Test
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calculator Modal */}
      {showCalculator && (
        <CalculatorModal
          display={calcDisplay}
          expression={calcExpression}
          onClose={handleCalculatorClose}
          onNumber={handleNumber}
          onOperator={handleOperator}
          onEquals={handleEquals}
          onClear={handleClear}
          onBackspace={handleBackspace}
          onChangeSign={handleChangeSign}
          onSquareRoot={handleSquareRoot}
          onPercentage={handlePercentage}
          onReciprocal={handleReciprocal}
          onMemoryClear={handleMemoryClear}
          onMemoryRecall={handleMemoryRecall}
          onMemoryStore={handleMemoryStore}
          onMemoryAdd={handleMemoryAdd}
          onMemorySubtract={handleMemorySubtract}
        />
      )}

      {/* Question Paper Modal */}
      {showQuestionPaper && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowQuestionPaper(false)}>
          <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Blue Header Bar */}
            <div className="bg-blue-600 text-white px-6 py-3 rounded-t-lg flex items-center justify-between">
              <span className="text-lg font-medium">Question Paper</span>
              <button
                onClick={() => setShowQuestionPaper(false)}
                className="text-white hover:text-gray-200 font-bold text-xl"
              >
                ×
              </button>
            </div>

            {/* Red Warning Message */}
            <div className="bg-red-50 border-l-4 border-red-500 px-6 py-3">
              <p className="text-red-700 font-medium text-sm">
                Note that timer is running. Kindly close the question paper page to attend the questions.
              </p>
            </div>

            {/* Section Title */}
            <div className="px-6 py-4 bg-white">
              <h2 className="text-2xl font-bold text-blue-600">
                {hasSections && selectedSectionIndex !== null
                  ? (testInfo.sections[selectedSectionIndex]?.name || 'Questions')
                  : (testInfo?.name || testInfo?.title || 'Questions')}
              </h2>
            </div>

            {/* Questions Content - Scrollable */}
            <div className="flex-1 overflow-y-auto px-6 pb-6">
              <div className="space-y-6">
                {filteredQuestions.map((question, index) => {
                  const actualQuestionNumber = hasSections && selectedSectionIndex !== null
                    ? parseInt(testInfo.sections[selectedSectionIndex]?.startQuestion || 1) + index
                    : index + 1;
                  
                  const markingScheme = question.markingScheme || { correct: 3.00, incorrect: 1.00 };
                  const correctMarks = markingScheme.correct || 3.00;
                  const negativeMarks = Math.abs(markingScheme.incorrect || 1.00);

                  return (
                    <div key={question.id || index}>
                      {/* Question Number and Text */}
                      <div className="mb-3">
                        <span className="font-semibold text-lg">Q.{actualQuestionNumber})</span>
                        <div className="mt-2 text-gray-800">
                          <KatexRenderer content={question.question} />
                        </div>
                      </div>

                      {/* Options */}
                      {question.options && question.options.length > 0 && (
                        <div className="ml-4 mb-3 space-y-1">
                          {question.options.map((option, optIndex) => (
                            <div key={optIndex} className="flex items-start">
                              <span className="mr-2 font-medium">{String.fromCharCode(65 + optIndex)}.</span>
                              <div className="text-gray-800">
                                <KatexRenderer content={option} />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Marking Scheme */}
                      <div className="text-sm text-gray-600 mb-3">
                        Marks for correct answer {correctMarks.toFixed(2)} ; Negative Marks {negativeMarks.toFixed(2)}
                      </div>

                      {/* Separator Line */}
                      {index < filteredQuestions.length - 1 && (
                        <div className="border-t border-gray-300"></div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedTestInterface;
